import dayjs from "dayjs";
import {
  EducationType,
  EnrollmentStatus,
} from "../types/client-education-detail.types";
import type {
  EducationDetailDto,
  EnrollmentDto,
  ExamAttemptDto,
  VirtualClassroomDto,
  ScheduledTime,
} from "../types/client-education-detail.types";
import {
  isRequestCollectionOpen,
  requestCollectionOpensAt,
} from "./request-collection";

export type CtaVariant = "primary" | "outlined" | "disabled" | "success" | "warning";

export interface CtaDescriptor {
  labelKey: string;
  fallbackLabel: string;
  /** Optional interpolation values forwarded to t(labelKey, …) */
  labelInterpolations?: Record<string, unknown>;
  variant: CtaVariant;
  pulse?: boolean;
  action:
    | { kind: "none" }
    | { kind: "reapply" }
    | { kind: "joinVirtualClassroom"; classroomId: string }
    | { kind: "scrollTo"; sectionId: string; highlight?: string }
    | { kind: "continueContent" }
    | { kind: "goExam" }
    | { kind: "goSurvey" }
    | { kind: "openCertificate" }
    | { kind: "completeManually" };
  hintKey?: string;
  hint?: string;
}

export interface ResolveCtaInput {
  education: EducationDetailDto;
  enrollment: EnrollmentDto | null;
  examAttempts: ExamAttemptDto[];
  now?: Date;
}

const MS_PER_MIN = 60_000;

function parseScheduledTimeToRange(
  st: ScheduledTime
): { start: Date; end: Date } | null {
  if (!st.date || !st.startTime || !st.endTime) return null;
  const start = dayjs(`${st.date}T${st.startTime}`);
  const end = dayjs(`${st.date}T${st.endTime}`);
  if (!start.isValid() || !end.isValid()) return null;
  return { start: start.toDate(), end: end.toDate() };
}

function findActiveClassroom(
  vcs: VirtualClassroomDto[] | undefined,
  now: Date
): { vc: VirtualClassroomDto; withinWindow: boolean } | null {
  if (!vcs?.length) return null;
  for (const vc of vcs) {
    const times = vc.scheduledTimes ?? [];
    for (const st of times) {
      const range = parseScheduledTimeToRange(st);
      if (!range) continue;
      const windowStart = range.start.getTime() - 15 * MS_PER_MIN;
      const windowEnd = range.end.getTime();
      if (now.getTime() >= windowStart && now.getTime() <= windowEnd) {
        return { vc, withinWindow: true };
      }
    }
  }
  return null;
}

function hasTodaySchedule(
  times: ScheduledTime[] | undefined,
  now: Date
): boolean {
  if (!times?.length) return false;
  const today = dayjs(now).format("YYYY-MM-DD");
  return times.some((t) => t.date === today);
}

function hasExam(education: EducationDetailDto): boolean {
  return !!education.exam?.id;
}

function hasSurvey(education: EducationDetailDto): boolean {
  return !!education.survey;
}

export function resolveCta(input: ResolveCtaInput): CtaDescriptor {
  const { education, enrollment, examAttempts } = input;
  const now = input.now ?? new Date();
  const type = education.type;

  // Eğitmen görünümü: enrollment kaydı YOKTUR, dolayısıyla aşağıdaki katalog
  // dalına düşüyordu ve eğitime kendi eğitmenine "Başvur" deniyordu — canlı
  // derse girecek buton hiç üretilmiyordu ("Katıl yeri yok"). Eğitmenin tek
  // aksiyonu dersi başlatmak/derse katılmaktır.
  if (education.isInstructor === true && !enrollment) {
    const active = findActiveClassroom(education.virtualClassrooms, now);
    if (active?.withinWindow) {
      const started = active.vc.hasActiveSession !== false;
      return {
        labelKey: started
          ? "clientEducationDetail.cta.instructorJoinLive"
          : "clientEducationDetail.cta.instructorStartLesson",
        fallbackLabel: "",
        variant: "primary",
        pulse: !started,
        action: { kind: "joinVirtualClassroom", classroomId: active.vc.id },
        hintKey: started ? undefined : "clientEducationDetail.cta.instructorStartLessonHint",
      };
    }
    return {
      labelKey: "clientEducationDetail.cta.instructorIdle",
      fallbackLabel: "",
      variant: "disabled",
      action: { kind: "none" },
      hintKey: "clientEducationDetail.cta.instructorIdleHint",
    };
  }

  if (!enrollment) {
    // Katalog modu: kullanıcı henüz başvurmamış.
    // Talep toplama penceresi açıksa "Başvur" primary CTA'sı döner; kapalıysa
    // duruma göre disabled/hint'li metin döner — böylece sidebar ve
    // ActiveStageCard yanıltıcı "kayıtlı değilsiniz" placeholder'ına düşmez.
    if (isRequestCollectionOpen(education, now)) {
      return {
        labelKey: "clientEducationDetail.cta.apply",
        fallbackLabel: "",
        variant: "primary",
        action: { kind: "reapply" },
      };
    }
    const opensAt = requestCollectionOpensAt(education, now);
    if (opensAt) {
      return {
        labelKey: "clientEducationDetail.cta.rcOpensAt",
        fallbackLabel: "",
        labelInterpolations: { date: dayjs(opensAt).format("DD MMM YYYY") },
        variant: "disabled",
        action: { kind: "none" },
        hintKey: "clientEducationDetail.cta.rcOpensAtHint",
      };
    }
    return {
      labelKey: "clientEducationDetail.cta.rcClosed",
      fallbackLabel: "",
      variant: "disabled",
      action: { kind: "none" },
      hintKey: "clientEducationDetail.cta.rcClosedHint",
    };
  }

  const status = enrollment.status;

  if (status === EnrollmentStatus.REQUESTED || status === EnrollmentStatus.APPROVED_BRANCH_MANAGER) {
    return { labelKey: "clientEducationDetail.cta.pending", fallbackLabel: "", variant: "disabled", action: { kind: "none" } };
  }

  // Yönetici eğitim kapanışında "tamamlamadı" işaretledi: kullanıcı ilerleyemez,
  // CTA gizli kalmalı. Detay sayfası NotCompletedBanner ile bilgilendirme yapar.
  if (status === EnrollmentStatus.NOT_COMPLETED) {
    return {
      labelKey: "clientEducationDetail.cta.notCompleted",
      fallbackLabel: "",
      variant: "disabled",
      action: { kind: "none" },
      hintKey: "clientEducationDetail.cta.notCompletedHint",
    };
  }

  // Onaylı ama startDate henüz gelmedi → backend de video/exam/survey endpoint'lerini
  // EDUCATION_NOT_YET_STARTED ile reddeder. UI tarafında CTA'yı disabled yap, tarih göster.
  if (education.startDate) {
    const start = dayjs(education.startDate);
    if (start.isValid() && start.toDate().getTime() > now.getTime()) {
      return {
        labelKey: "clientEducationDetail.cta.notYetStarted",
        fallbackLabel: "",
        labelInterpolations: { date: start.format("DD MMM YYYY") },
        variant: "disabled",
        action: { kind: "none" },
        hintKey: "clientEducationDetail.cta.notYetStartedHint",
      };
    }
  }
  if (status === EnrollmentStatus.REJECTED_BY_BRANCH_MANAGER || status === EnrollmentStatus.REJECTED_BY_CENTRAL_MANAGER) {
    return { labelKey: "clientEducationDetail.cta.reapply", fallbackLabel: "", variant: "outlined", action: { kind: "reapply" } };
  }
  if (status === EnrollmentStatus.CANCELLED) {
    return { labelKey: "clientEducationDetail.cta.reapplyAfterCancel", fallbackLabel: "", variant: "outlined", action: { kind: "reapply" } };
  }

  if (type === EducationType.ONLINE) {
    const active = findActiveClassroom(education.virtualClassrooms, now);
    if (active?.withinWindow) {
      return {
        labelKey: "clientEducationDetail.cta.joinLive",
        fallbackLabel: "",
        variant: "primary",
        pulse: true,
        action: { kind: "joinVirtualClassroom", classroomId: active.vc.id },
      };
    }
  }

  if (type === EducationType.ON_SITE) {
    if (hasTodaySchedule(education.scheduledTimes ?? undefined, now)) {
      return {
        labelKey: "clientEducationDetail.cta.sessionDetails",
        fallbackLabel: "",
        variant: "primary",
        action: { kind: "scrollTo", sectionId: "scheduling", highlight: "today" },
      };
    }
  }

  const isContentType = type === EducationType.UNIT || type === EducationType.NONUNIT;
  const progress = enrollment.progressPercent ?? 0;

  if (status === EnrollmentStatus.ENROLLED && isContentType) {
    if (progress < 100) {
      return { labelKey: "clientEducationDetail.cta.continueContent", fallbackLabel: "", variant: "primary", action: { kind: "continueContent" } };
    }
    // progress=100 but status still ENROLLED means backend auto-transition in flight;
    // fall through to fallback CTA — will resolve on next refresh.
  }

  if (status === EnrollmentStatus.ENROLLED && (type === EducationType.ONLINE || type === EducationType.ON_SITE)) {
    return {
      labelKey: "clientEducationDetail.cta.viewSchedule",
      fallbackLabel: "",
      variant: "outlined",
      action: {
        kind: "scrollTo",
        sectionId: type === EducationType.ONLINE ? "virtualClassrooms" : "scheduling",
      },
    };
  }

  if (status === EnrollmentStatus.EXAM_STARTED) {
    return { labelKey: "clientEducationDetail.cta.resumeExam", fallbackLabel: "", variant: "warning", action: { kind: "goExam" } };
  }

  if (status === EnrollmentStatus.CONTENT_COMPLETED && hasExam(education)) {
    if (examAttempts.length === 0) {
      return { labelKey: "clientEducationDetail.cta.startExam", fallbackLabel: "", variant: "primary", action: { kind: "goExam" } };
    }
  }

  if (status === EnrollmentStatus.EXAM_FAILED) {
    return { labelKey: "clientEducationDetail.cta.retryExam", fallbackLabel: "", variant: "primary", action: { kind: "goExam" } };
  }

  if (status === EnrollmentStatus.SURVEY_STARTED) {
    if (hasSurvey(education)) {
      return { labelKey: "clientEducationDetail.cta.resumeSurvey", fallbackLabel: "", variant: "primary", action: { kind: "goSurvey" } };
    }
    // Anket silinmiş ama enrollment SURVEY_STARTED'ta takılı — kullanıcı sessiz
    // pasif buton görmek yerine açıkça neden ilerleyemediğini anlamalı.
    return {
      labelKey: "clientEducationDetail.cta.surveyMissing",
      fallbackLabel: "",
      variant: "warning",
      action: { kind: "none" },
      hintKey: "clientEducationDetail.cta.surveyMissingHint",
    };
  }

  if (status === EnrollmentStatus.EXAM_COMPLETED && hasSurvey(education) && !enrollment.surveyCompletedAt) {
    return { labelKey: "clientEducationDetail.cta.startSurvey", fallbackLabel: "", variant: "primary", action: { kind: "goSurvey" } };
  }

  if (status === EnrollmentStatus.COMPLETED) {
    if (education.certificateId) {
      return { labelKey: "clientEducationDetail.cta.viewCertificate", fallbackLabel: "", variant: "primary", action: { kind: "openCertificate" } };
    }
    return { labelKey: "clientEducationDetail.cta.completedBadge", fallbackLabel: "", variant: "success", action: { kind: "none" } };
  }

  // Rule #16 (manual complete): ENROLLED with nothing else to do.
  // Applies when ENROLLED status and no exam/survey defined.
  // Catches edge cases like ONLINE with no virtual classrooms, or any type
  // without exam/survey where prior rules didn't match.
  if (status === EnrollmentStatus.ENROLLED && !hasExam(education) && !hasSurvey(education)) {
    return { labelKey: "clientEducationDetail.cta.completeTraining", fallbackLabel: "", variant: "primary", action: { kind: "completeManually" } };
  }

  return { labelKey: "clientEducationDetail.cta.active", fallbackLabel: "", variant: "disabled", action: { kind: "none" } };
}
